import React, { useId, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { Popover, Tooltip } from 'antd'
import { useIntl } from 'react-intl'
import type dayjs from 'dayjs'

import FormattedDate from '@vacationtracker/shared/components/formatted-date'
import { LocaleEnum } from '@vacationtracker/shared/types/i18n'

import { Button } from '../../design-system/Button'
import { StatusBadge } from '../../design-system/StatusBadge'
import { Text } from '../../design-system/Text'
import IntlMessages from '../../util/IntlMessages'
import { useHideCrispWhile } from '../../hooks/use-hide-crisp-while'
import { EditableCell } from '../inline-edit/grid-cell'
import { libraryRuleText } from './library-rule'
import type { LibraryMatch } from './library-rule'
import { HolidayRow, HolidayTag, cellAccess } from './logic'
import { RecurrenceSettings, recurrenceChangePayload, savedRecurrenceSummary } from './recurrence'
import { RecurrenceEditor, useRecurrenceText, useRecurrenceValue } from './RecurrenceEditor'

// Pinned top-left: hidden text left in flow makes the app root scroll.
const SR_ONLY: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
}

const TAG_LABEL: Record<HolidayTag, string> = {
  public: 'holidays.typePublic',
  bank: 'holidays.typeBank',
  school: 'holidays.typeSchool',
  optional: 'holidays.typeOptional',
  observance: 'holidays.typeObservance',
  custom: 'app.holiday.typeCustom',
}

const WITH_WEEKDAY = 'dddd, MMMM D'

interface ExplainedProps {
  explanation: React.ReactNode
  style?: React.CSSProperties
  children: React.ReactNode
}

// Focusable, so the explanation is reachable without a pointer. The tooltip sits on an outer span:
// antd overwrites its child's aria-describedby with the id of a popup that exists only while open.
function Explained({ explanation, style, children }: ExplainedProps): React.ReactElement {
  const id = useId()
  return (
    <Tooltip title={explanation}>
      <span style={{ display: 'inline-flex' }}>
        <span tabIndex={0} aria-describedby={id} style={{ display: 'inline-flex', position: 'relative', ...style }}>
          {children}
          <span id={id} style={SR_ONLY}>
            {explanation}
          </span>
        </span>
      </span>
    </Tooltip>
  )
}

// A text cursor over a label would read as something to act on.
const NOT_ACTIONABLE: React.CSSProperties = { cursor: 'default' }

export interface WithReasonProps {
  reason?: string
  children: React.ReactElement
}

// The wrapper takes hover and focus because a disabled control takes neither.
export function WithReason({ reason, children }: WithReasonProps): React.ReactElement {
  if (!reason) return children
  return <Explained explanation={reason}>{children}</Explained>
}

export interface TypeTagProps {
  tag: HolidayTag
}

export function TypeTag({ tag }: TypeTagProps): React.ReactElement {
  return (
    <StatusBadge tone="neutral">
      <IntlMessages id={TAG_LABEL[tag]} />
    </StatusBadge>
  )
}

export interface HolidayDatesProps {
  row: Pick<HolidayRow, 'date' | 'endDate' | 'isHalfDay'>
  locale: LocaleEnum
}

export function HolidayDates({ row, locale }: HolidayDatesProps): React.ReactElement {
  return (
    <>
      <FormattedDate locale={locale} value={row.date} format={WITH_WEEKDAY} />
      {row.isHalfDay && (
        <>
          {' '}
          <IntlMessages id="components.leavesColumns.halfDayHoliday" />
        </>
      )}
      {row.endDate && row.endDate !== row.date && (
        <>
          {' – '}
          <FormattedDate locale={locale} value={row.endDate} format={WITH_WEEKDAY} />
        </>
      )}
    </>
  )
}

export interface RecurrenceCellProps {
  row: HolidayRow
  locationId: string
  /** Which cell is open, grid-wide. */
  cellKey: string
  /** False for a location the reader cannot edit: the value only, no lock. */
  canEdit: boolean
  /** An imported row's date-holidays rule, from libraryRulesOf. */
  libraryRule?: LibraryMatch
  today?: dayjs.Dayjs
  /** Saves the pick, only one that changes something; resolves with the key the saved mark also goes to. */
  onCommit: (settings: RecurrenceSettings | null) => Promise<string | void>
}

// NoirPro's Medium face: the theme's `fontWeight.medium` is 600, and no token names 500.
const POPOVER_TITLE: React.CSSProperties = { fontWeight: 500 }

const RECURRENCE_POPOVER_CLASS = 'vt-recurrence-popover'

// antd keeps the popover under the cell's left edge, which on a phone can leave half of it off-screen.
// Below 576px it is pinned inside the screen instead, with no arrow pointing at the wrong place.
// Rendered by the tab with the other panel styles: a style element inside the cell would count as its text.
export const recurrencePopoverStyles = `
@media (max-width: 575px) {
  .${RECURRENCE_POPOVER_CLASS} { left: 8px !important; right: auto !important; max-width: calc(100vw - 16px); }
  .${RECURRENCE_POPOVER_CLASS} .ant-popover-arrow { display: none; }
}
`

/** Only a change the backend accepts is ever posted. */
export function RecurrenceCell({ row, locationId, cellKey, canEdit, libraryRule: rule, today, onCommit }: RecurrenceCellProps): React.ReactElement {
  const intl = useIntl()
  const { formatMessage } = intl
  const toText = useRecurrenceText()
  const toValue = useRecurrenceValue()
  const libraryRule = useMemo(() => (row.recurring ? libraryRuleText(intl, rule) : undefined), [intl, row.recurring, rule])
  const value = libraryRule?.short ?? toValue(row)
  const title = formatMessage({ id: 'holidaysTab.recurrence.header' })
  // A past custom holiday that does not repeat has nothing left to pick: it cannot start repeating.
  const { editable, lockReason: lock } = cellAccess({ field: 'recurring', row, canEdit, today })
  const summary = row.recurring ? (libraryRule?.summary ?? toText(savedRecurrenceSummary(row))) : undefined

  let display: React.ReactNode = value
  if (summary && !lock) {
    // Inside the editable cell's button a second tab stop would nest one control in another.
    display = editable ? (
      <Tooltip title={summary}>
        <span>{value}</span>
      </Tooltip>
    ) : (
      <Explained explanation={summary} style={NOT_ACTIONABLE}>
        {value}
      </Explained>
    )
  }

  return (
    <EditableCell<RecurrenceSettings | null>
      cellKey={cellKey}
      editable={editable}
      fit="content"
      lockReason={lock}
      lockReasonOnCell
      editingRing={false}
      affordance="pencil"
      editLabel={formatMessage({ id: 'locationGeneral.editField' }, { field: title })}
      display={display}
      errorMessage={formatMessage({ id: 'error.generic' })}
      renderEditor={({ commit, cancel }) => (
        <Popover
          open
          title={<span style={POPOVER_TITLE}>{title}</span>}
          trigger="click"
          placement="bottomLeft"
          classNames={{ root: RECURRENCE_POPOVER_CLASS }}
          destroyOnHidden
          onOpenChange={(next) => {
            if (!next) cancel()
          }}
          content={
            <RecurrenceEditor
              row={row}
              libraryRule={libraryRule}
              onCancel={cancel}
              onSave={(settings) => {
                if (recurrenceChangePayload({ row, locationId, settings })) {
                  commit(settings)
                } else {
                  cancel()
                }
              }}
            />
          }
        >
          <span>{value}</span>
        </Popover>
      )}
      onCommit={onCommit}
    />
  )
}

export interface SelectionBarProps {
  count: number
  onClear: () => void
  onDelete: () => void
}

/**
 * The Users table's selection bar (BulkActionsBar): its markup and its global stylesheet, floating
 * at the bottom of the viewport. Portalled for the same reason: inside antd Tabs an ancestor's
 * transform makes position:fixed mean the pane.
 */
export function SelectionBar({ count, onClear, onDelete }: SelectionBarProps): React.ReactElement {
  // On a narrow screen the bar reaches the bottom-right corner, where the Crisp launcher would cover it
  useHideCrispWhile(true)
  return createPortal(
    <div className="vt-users-bulkbar">
      <span className="vt-users-bulkbar-count">
        <Text size="lg">
          <IntlMessages id="holidaysTab.bulkDelete.selected" values={{ count }} />
        </Text>
      </span>
      <Button variant="text" size="small" onClick={onClear}>
        <IntlMessages id="users.table.clearSelection" />
      </Button>
      <Button variant="outline-danger" onClick={onDelete}>
        <IntlMessages id="app.delete" />
      </Button>
    </div>,
    document.body
  )
}
